import React, { useState } from 'react';
import { Modal, Button, Typography, Space, Alert, Tag } from 'antd';
import { useIntl } from 'react-intl';
import { useScopedNavigate } from './MarketerView/useScopedNavigate';
import { CrownOutlined, RocketOutlined } from '@ant-design/icons';
import { useSubscriptionFeatures, SubscriptionFeature } from '../hooks/useSubscriptionFeatures';
import { SubscriptionPlanEnum } from '../services/nestjs-base-api/types';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';

const { Title, Paragraph } = Typography;

interface FeatureUpgradeModalProps {
    visible: boolean;
    onClose: () => void;
    featureName: string;
    featureDescription?: string;
    // New subscription-related props
    brandId?: number;
    brandName?: string;
    subscriptionFeature?: SubscriptionFeature;
    requiredPlan?: SubscriptionPlanEnum;
}

const FeatureUpgradeModal: React.FC<FeatureUpgradeModalProps> = ({
    visible,
    onClose,
    featureName,
    featureDescription,
    brandId,
    brandName,
    subscriptionFeature,
    requiredPlan,
}) => {
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    
    const { requiresUpgrade, plan: currentPlan } = useSubscriptionFeatures(brandId);

    // Determine if this is a subscription-based feature or legacy feature
    const isSubscriptionFeature = subscriptionFeature && brandId;
    
    let upgradeInfo;
    if (isSubscriptionFeature) {
        upgradeInfo = requiresUpgrade(subscriptionFeature);
    }

    const handleUpgrade = () => {
        if (isSubscriptionFeature) {
            setShowSubscriptionModal(true);
        } else {
            // Legacy behavior for old feature system
            navigate('/dashboard/organisation?tab=features#upgrade');
            onClose();
        }
    };

    const handleSubscriptionUpgrade = (selectedPlan: SubscriptionPlanEnum) => {
        setShowSubscriptionModal(false);
        navigate('/dashboard/organisation?tab=billing');
        onClose();
    };

    return (
        <>
            <Modal
                title={null}
                open={visible}
                onCancel={onClose}
                footer={null}
                width={500}
                centered
            >
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <CrownOutlined style={{ fontSize: 36, color: '#faad14', marginBottom: 12 }} />
                    
                    <Title level={4} style={{ marginBottom: 8 }}>
                        {isSubscriptionFeature ? `${featureName} Requires Upgrade` : intl.formatMessage({ id: 'featureUpgrade.title' })}
                    </Title>
                    
                    {isSubscriptionFeature && upgradeInfo ? (
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Paragraph style={{ color: '#666', marginBottom: 16 }}>
                                {upgradeInfo.message}
                                {brandName && ` for ${brandName}`}
                            </Paragraph>
                            
                            {currentPlan && (
                                <div>
                                    <Paragraph style={{ margin: 0 }}>
                                        Current Plan: <Tag color="blue">{currentPlan}</Tag>
                                    </Paragraph>
                                    <Paragraph>
                                        Required Plan: <Tag color="purple">{upgradeInfo.requiredPlan}</Tag>
                                    </Paragraph>
                                </div>
                            )}
                        </Space>
                    ) : (
                        <>
                            <Paragraph style={{ color: '#666', marginBottom: 16 }}>
                                {featureDescription || intl.formatMessage({ 
                                    id: 'featureUpgrade.shortDescription' 
                                }, { featureName })}
                            </Paragraph>

                            <Alert
                                message={intl.formatMessage({ id: 'featureUpgrade.pricingModel' })}
                                type="info"
                                style={{ marginBottom: 20, textAlign: 'left' }}
                            />
                        </>
                    )}

                    <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
                        <Button 
                            size="large" 
                            onClick={onClose}
                        >
                            {intl.formatMessage({ id: 'featureUpgrade.cancel' })}
                        </Button>
                        <Button 
                            type="primary" 
                            size="large" 
                            icon={isSubscriptionFeature ? <RocketOutlined /> : <CrownOutlined />}
                            onClick={handleUpgrade}
                        >
                            {isSubscriptionFeature ? 
                                `Upgrade to ${upgradeInfo?.requiredPlan || 'Premium'}` : 
                                intl.formatMessage({ id: 'featureUpgrade.upgrade' })
                            }
                        </Button>
                    </Space>
                </div>
            </Modal>

            {isSubscriptionFeature && upgradeInfo && (
                <SubscriptionUpgradeModal
                    visible={showSubscriptionModal}
                    onCancel={() => setShowSubscriptionModal(false)}
                    onUpgrade={handleSubscriptionUpgrade}
                    currentPlan={currentPlan}
                    requiredPlan={upgradeInfo.requiredPlan}
                    featureName={featureName}
                    brandName={brandName}
                />
            )}
        </>
    );
};

export default FeatureUpgradeModal;